/**
 * Where a horizontal swipe should come to rest, given where it is and how fast
 * it was going when the finger lifted.
 *
 * `velocity` is the scroll velocity in px/ms (positive = scrolling right). The
 * swipe is projected forward `projectionMs` — roughly how far the phone's own
 * momentum would have carried it — and rounded to the nearest column. The last
 * position is `max` even when it isn't a whole number of columns in, so the end
 * of the grid is always reachable.
 */
export function pickSnapTarget({
  scrollLeft,
  velocity,
  step,
  max,
  projectionMs = 140,
}: {
  scrollLeft: number;
  velocity: number;
  step: number;
  max: number;
  projectionMs?: number;
}): number {
  if (step <= 0 || max <= 0) return 0;
  const projected = scrollLeft + velocity * projectionMs;
  const clamped = Math.min(Math.max(projected, 0), max);
  return Math.min(Math.round(clamped / step) * step, max);
}

/** Velocity (px/ms) over the most recent `windowMs` of touch samples. */
export function releaseVelocity(
  samples: { t: number; x: number }[],
  windowMs = 100
): number {
  const last = samples.at(-1);
  if (!last) return 0;
  const first = samples.find((s) => last.t - s.t <= windowMs) ?? last;
  const dt = last.t - first.t;
  // Scroll moves opposite to the finger.
  return dt > 0 ? -(last.x - first.x) / dt : 0;
}

/** Animation length: quick, and a little longer for a longer trip. */
export function snapDuration(distance: number): number {
  return 110 + Math.min(90, Math.abs(distance) * 0.25);
}
